import React, { useState, useEffect,useRef } from 'react';
import { Menu, X, Phone, Mail, Clock, Wrench,Scissors, Home, AlertCircle, Zap, Star, Award, Shield, Calculator, Search,Truck, ArrowLeft, Package, CloudRain, ChevronRight, ArrowRight, Users, Building, CheckCircle, TrendingUp, ChevronLeft, Grid, List, ShoppingCart, HardHat, PaintBucket, Droplets, Thermometer, Layers, Send, Calendar, DollarSign, Bath, Target, Hammer, Handshake, Cpu, Microscope, Quote, MapPin} from 'lucide-react';

import { Trabajo, Producto, Servicio, PhoneNumber } from '../types';
import { apiCall, API_BASE_URL, formatPrecio } from '../utils';
import { serviciosDetallados } from '../pages/HomePage';
import PhoneSelector from '../components/PhoneSelector';

// ==================== PÁGINA DE SERVICIOS ====================
// IMPORTANTE: Este componente necesita recibir estas props desde App.tsx
interface ServiciosPageProps {
  navigateToPage?: (page: string) => void;
  PhoneSelector?: React.ComponentType<{ isOpen: boolean; onClose: () => void }>;
}

export default function ServiciosPage({ navigateToPage, PhoneSelector }: ServiciosPageProps) {
  const [selectedServicio, setSelectedServicio] = useState<string | null>(null);
  const [trabajosRelacionados, setTrabajosRelacionados] = useState<any[]>([]);
  const [loadingTrabajos, setLoadingTrabajos] = useState(false);
  const [showPhoneSelector, setShowPhoneSelector] = useState(false);

  // Función para obtener trabajos relacionados
  const fetchTrabajosRelacionados = async (categoria: string) => {
    setLoadingTrabajos(true);
    try {
      // Usar la URL base de la API (en producción es /api vía nginx; en local, localhost:8000)
      const response = await fetch(`${API_BASE_URL}/trabajos?categoria=${categoria}&limit=3`);
      if (response.ok) {
        const data = await response.json();
        setTrabajosRelacionados(data);
      }
    } catch (error) {
      console.error('Error al cargar trabajos:', error);
      setTrabajosRelacionados([]);
    } finally {
      setLoadingTrabajos(false);
    }
  };

  // Cargar trabajos relacionados cuando se selecciona un servicio
  React.useEffect(() => {
    if (selectedServicio) {
      const servicio = serviciosDetallados.find(s => s.id === selectedServicio);
      if (servicio && servicio.categoriaTrabajo) {
        fetchTrabajosRelacionados(servicio.categoriaTrabajo);
      }
    }
  }, [selectedServicio]);


  // Efecto para manejar eventos personalizados
  useEffect(() => {
    const handleSelectServicio = (event: CustomEvent) => {
      setSelectedServicio(event.detail);
      window.scrollTo(0, 0);
    };

    window.addEventListener('selectServicio', handleSelectServicio as EventListener);
    return () => {
      window.removeEventListener('selectServicio', handleSelectServicio as EventListener);
    };
  }, []);


  // Si hay un servicio seleccionado, mostrar su página de detalle
  if (selectedServicio) {
    const servicio = serviciosDetallados.find(s => s.id === selectedServicio);
    
    if (!servicio) return null;

    return (
      <div className="min-h-screen bg-gray-50">
        {/* PhoneSelector Modal */}
        {PhoneSelector && (
          <PhoneSelector 
            isOpen={showPhoneSelector} 
            onClose={() => setShowPhoneSelector(false)} 
          />
        )}
        {/* Hero Section - Gradiente corporativo */}
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-20">
          {/* Decoraciones geométricas sutiles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-blue-500/10 -translate-y-1/2 translate-x-1/4"></div>
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-cyan-500/10 translate-y-1/2 -translate-x-1/4"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-600/5"></div>
            {/* Líneas decorativas */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Botón volver */}
            <button
              onClick={() => setSelectedServicio(null)}
              className="flex items-center text-blue-300/80 hover:text-white mb-8 transition-colors group"
            >
              <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              Volver a servicios
            </button>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-2xl">
                {/* Línea decorativa + etiqueta */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-[2px] w-12 bg-blue-400"></div>
                  <span className="text-sm font-bold text-blue-400 uppercase tracking-widest">Servicio</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {servicio.titulo}
                </h1>
                <p className="text-xl text-blue-100/80 leading-relaxed">
                  {servicio.descripcion}
                </p>
              </div>

              {/* CTA en el hero */}
              <div className="flex flex-col gap-3 lg:min-w-[220px]">
                <button
                  onClick={() => navigateToPage && navigateToPage('contacto')}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-0.5"
                >
                  Solicitar Presupuesto
                </button>
                <button
                  onClick={() => setShowPhoneSelector(true)}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Llamar Ahora
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Contenido principal */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Columna principal - Descripción */}
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Sobre este servicio</h2>
                  <div className="text-lg text-gray-700 leading-relaxed space-y-4">
                    {servicio.descripcionLarga
                      .replace(/<[^>]+>/g, ' ')
                      .split(/\n+/)
                      .map((p, i) => p.trim() && <p key={i}>{p.trim()}</p>)}
                  </div>

                  
                  {/* Puntos clave */}
                  <div className="mt-8 grid sm:grid-cols-2 gap-4">
                    {servicio.puntosClave.map((punto, idx) => (
                      <div key={idx} className="flex items-center bg-blue-50 rounded-lg p-4">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
                        <span className="text-gray-800 font-medium">{punto}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Proyectos relacionados */}
                {servicio.categoriaTrabajo && (
                  <div className="bg-white rounded-2xl shadow-lg p-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Proyectos Realizados</h2>
                    
                    {loadingTrabajos ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      </div>
                    ) : trabajosRelacionados.length > 0 ? (
                      <>
                        <div className="grid md:grid-cols-3 gap-6 mb-6">
                          {trabajosRelacionados.map((trabajo) => (
                            <div 
                              key={trabajo.id} 
                              className="group cursor-pointer"
                              onClick={() => {
                                if (navigateToPage) {
                                  // Navegar a la página de trabajos
                                  navigateToPage('proyectos');
                                  
                                  // Esperar a que la página cargue y hacer scroll al trabajo
                                  setTimeout(() => {
                                    const element = document.getElementById(`trabajo-${trabajo.id}`);
                                    if (element) {
                                      // Hacer scroll suave al elemento
                                      element.scrollIntoView({ 
                                        behavior: 'smooth', 
                                        block: 'center' 
                                      });
                                      
                                      // Añadir la misma animación que el buscador
                                      element.classList.add('highlight-animation');
                                      setTimeout(() => element.classList.remove('highlight-animation'), 2000);
                                    }
                                  }, 800);
                                }
                              }}
                            >
                              <div className="relative h-48 overflow-hidden rounded-lg mb-3">
                                <img
                                  src={trabajo.imagenes_despues?.[0] || trabajo.imagenes_antes?.[0] || '/imagen-no-disponible.svg'}
                                  alt={trabajo.titulo}
                                  loading="lazy"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                              <h4 className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">{trabajo.titulo}</h4>
                              <p className="text-sm text-gray-600 flex items-center mt-1">
                                <MapPin className="h-4 w-4 mr-1" />
                                {trabajo.ubicacion}
                              </p>
                            </div>
                          ))}
                        </div>
                        <button 
                          onClick={() => {
                            if (navigateToPage) {
                              navigateToPage('proyectos');
                              setTimeout(() => {
                                const event = new CustomEvent('filterTrabajosByCategory', { detail: servicio.categoriaTrabajo });
                                window.dispatchEvent(event);
                              }, 500);
                            }
                          }}
                          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                          Ver Más Proyectos de {servicio.titulo}
                        </button>
                      </>
                    ) : (
                      <p className="text-gray-600 text-center py-8">No hay proyectos disponibles en este momento.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Columna lateral - Servicios incluidos y CTA */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-8 sticky top-4">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Este servicio incluye</h3>
                  <ul className="space-y-3 mb-8">
                    {servicio.serviciosIncluidos.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="border-t pt-6">
                    <p className="text-gray-600 mb-4">¿Necesitas este servicio?</p>
                    <button 
                      onClick={() => navigateToPage && navigateToPage('contacto')}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors mb-3">
                      Solicitar Presupuesto
                    </button>
                    <button 
                      onClick={() => setShowPhoneSelector(true)}
                      className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-bold hover:border-blue-600 hover:text-blue-600 transition-colors flex items-center justify-center">
                      <Phone className="mr-2 h-5 w-5" />
                      Llamar Ahora
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Vista principal de servicios
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      {/* PhoneSelector Modal */}
      {PhoneSelector && (
        <PhoneSelector 
          isOpen={showPhoneSelector} 
          onClose={() => setShowPhoneSelector(false)} 
        />
      )}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="h-[2px] w-10 bg-blue-600"></div>
          <span className="text-sm font-semibold text-blue-600 uppercase tracking-widest">Todo lo que tu obra necesita</span>
          <div className="h-[2px] w-10 bg-blue-600"></div>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Nuestros Servicios</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Ofrecemos una amplia gama de servicios de construcción y reforma con más de {new Date().getFullYear() - 1998} años de experiencia
        </p>
      </div>

      <div className="space-y-6">
        {serviciosDetallados.map((servicio) => (
          <div 
            key={servicio.id} 
            id={`servicio-${servicio.id}`}
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
          >
            <div className="md:flex">
              {/* Imagen */}
              <div className="md:w-1/3">
                <div className="h-full min-h-[280px] relative overflow-hidden">
                  <img
                    src={servicio.imagen}
                    alt={servicio.titulo}
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Contenido */}
              <div className="md:w-2/3 p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">{servicio.titulo}</h3>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed">{servicio.descripcion}</p>
                
                {/* Puntos clave destacados */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {servicio.puntosClave.slice(0, 3).map((punto, idx) => (
                    <div key={idx} className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">{punto}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setSelectedServicio(servicio.id);
                    window.scrollTo(0, 0);
                  }}
                  className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center group"
                >
                  Ver más información
                  <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA Final */}
      <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-12 text-center">
        <h2 className="text-3xl font-bold mb-4">¿No encuentras el servicio que necesitas?</h2>
        <p className="text-xl mb-8 text-blue-100">
          Contacta con nosotros y te daremos una solución personalizada
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            onClick={() => navigateToPage && navigateToPage('contacto')}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors">
            Solicitar Presupuesto
          </button>
          <button 
            onClick={() => setShowPhoneSelector(true)}
            className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors">
            <Phone className="inline mr-2" />
            Llamar Ahora
          </button>
        </div>
      </div>
    </div>
  );
}


